import { h } from '@omni-torn/ui';
import {
  basketRows,
  eggRows,
  eventCsvRows,
  eventInfo,
  eventWindow,
  EASTER_EGG_IDS,
  EVENT_ATTACK_ENERGY,
  FIND_KINDS,
  findKindInfo,
  presetRange,
  readFoundTotals,
  type FindKind,
  HALLOWEEN_BASKET_IDS,
  inRange,
  keyMeets,
  rangeText,
  readEggTotals,
  readEventLine,
  readTornCalendar,
  sumPerDay,
  summariseEvent,
  TORN_EVENTS,
  treatRates,
  type CalendarEntry,
  type DateRange,
  type EventLine,
  type TornEvent,
} from '@omni-torn/shared';
import { forgetLogText, readingLogText } from '../own-history';
import type { RefItem } from '@omni-torn/shared/torn-ref';
import { firstRead, LogReader } from '../log-kit/reader';
import { logStore, type LogStore } from '../log-kit/store';
import { csvButton, keptLogPanel, rangeFromAddress, rangePicker, setAddressQuery, zoomChart } from '../log-kit/ui';
import { readAttacks } from '../read';
import { server, session, torn } from '../session';
import { box, facts, gated, loading, money, num, problem, table, tabs, tct } from '../ui';
import { findsPanel } from './finds';
import { ref } from './torn';

/**
 * Events (You → Events, website roadmap S16 — torn.report's Christmas Town, Easter Eggs and Trick or Treat pages, Torn
 * Intel's basket guide and energy-to-treats), on the log-report kit (S09):
 *  - each event's dates from Torn's calendar (your key, public), with "Show these dates";
 *  - from your log (Full key), kept in this browser: what the event gave you — items at Torn's market value, money,
 *    treats — per day, per year, per kind of line, and the lines with CSV;
 *  - Christmas Town: finds by item and by what (chests, games); Easter: eggs by colour, and all time from personal stats;
 *  - Trick or Treat: the basket guide (Torn's own description and market value of each basket, and what yours gave) and
 *    energy to treats (Lite: treats from the log; Full: with your fights in the same dates — treats per hit and per 100
 *    energy).
 * Your own key only; nothing new goes to the Omni-Torn server (its 90-day log and fights copies, when tracking is on, fill in).
 * The Target finder's "Not in hospital" and "Elimination" presets are the event target lists (Tools → Target finder).
 */

const LOG_NEED = { level: 4, user: ['log'] } as const;
const FIGHTS_NEED = { level: 3, user: ['attacks'] } as const;

const tile = (label: string, value: string, data = '') => h('div', { class: 'tile', ...(data ? { 'data-tile': data } : {}) }, h('div', { class: 'tile-label' }, label), h('div', { class: 'tile-value' }, value));
const day = (ms: number) => new Date(ms).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
const hasRangeInAddress = () => /[?&](range|from|to)=/.test(location.search);

export async function renderEvents(view: HTMLElement): Promise<void> {
  const me = session.get();
  if (!me) return;
  const pid = me.playerId;
  const q = new URLSearchParams(location.search);
  let tab: TornEvent = TORN_EVENTS.find((e) => e.id === q.get('event'))?.id ?? 'christmas';
  // S38's tabs (Hunting, Dump, City finds): not yearly events — their default range is the last 30 days.
  let finds: FindKind | null = FIND_KINDS.find((k) => k.id === q.get('event'))?.id ?? null;
  // The range: the address's; else the event's dates once Torn's calendar is read (until then, everything kept).
  let range: DateRange = hasRangeInAddress() ? rangeFromAddress('all') : finds ? presetRange('30d', Date.now()) : { from: null, to: null };
  let rangeChosen = hasRangeInAddress();
  const eventParam = () => finds ?? (tab === 'christmas' ? null : tab);

  let calendar: CalendarEntry[] | null = null;
  let calendarProblem = '';
  let items = new Map<number, RefItem>();
  let eggTotals: { found: number | null; used: number | null } | null = null;
  let foundTotals: { city: number | null; dump: number | null } | null = null;
  let fights: Array<{ at: number; won: boolean }> | null = null;
  let fightsNote = '';

  const status = h('p', { class: 'muted small', 'data-events-status': '' });
  const body = h('div', {});
  view.replaceChildren(
    h('div', { class: 'page-head' }, h('h1', {}, 'Events'), h('p', { class: 'muted' }, 'Torn’s yearly events from your own log — Christmas Town, Trick or Treat and Easter eggs: what each gave you, every year — and your hunting, dump searches and city finds. Event target lists are in ', h('a', { href: '/targets', 'data-link': true }, 'Target finder'), ' (Not in hospital, Elimination).')),
    tabs(
      [...TORN_EVENTS.map((e) => ({ id: e.id, label: e.name })), ...FIND_KINDS.map((k) => ({ id: k.id, label: k.name }))],
      finds ?? tab,
      (id) => {
        finds = FIND_KINDS.find((k) => k.id === id)?.id ?? null;
        if (!finds) tab = id as TornEvent;
        if (!rangeChosen) range = finds ? presetRange('30d', Date.now()) : (windowRange() ?? { from: null, to: null });
        setAddressQuery(rangeChosen ? range : null, { event: eventParam() });
        draw();
      },
    ),
    status,
    body,
  );

  /** The dates to show by default: the event on now, else the last one (one still to come has nothing in your log yet). */
  const windowRange = (): DateRange | null => {
    if (!calendar) return null;
    const now = Date.now();
    const w = eventWindow(calendar, tab, now);
    if (!w || w.entry.start <= now) return w?.range ?? null;
    return eventWindow(calendar.filter((c) => c.start <= now), tab, now)?.range ?? null;
  };

  // ------------------------------------------------------------ the log (the kit)

  let store: LogStore | null = null;
  let reader: LogReader | null = null;
  let first: Promise<void> | null = null;
  const logNote = h('span', { class: 'muted small', 'data-log-note': '' });

  async function ensureReader(): Promise<LogReader> {
    if (reader) return reader;
    store = await logStore(pid);
    // The whole log (the same kept copy as Energy & nerve): event lines sit in several of Torn's categories.
    reader = new LogReader(store, { id: 'all' });
    return reader;
  }

  const values = () => new Map([...items.values()].map((i) => [i.id, i.marketValue] as const));
  const itemName = (id: number) => items.get(id)?.name ?? `Item ${id}`;

  function drawLog(): HTMLElement {
    const out = h('div', {}, loading(readingLogText()));
    void (async () => {
      try {
        const r = await ensureReader();
        first ??= firstRead(r).then(
          (t) => void (logNote.textContent = t),
          (err: unknown) => void logNote.replaceChildren(problem(err)),
        );
        await first;
        if (out.isConnected) await fillLog(out);
      } catch (err) {
        out.replaceChildren(problem(err));
      }
    })();
    return out;
  }

  async function fillLog(out: HTMLElement): Promise<void> {
    const r = await ensureReader();
    const all = (await store!.range({ from: null, to: null })).map(readEventLine).filter((l): l is EventLine => !!l && l.event === tab);
    const shown = all.filter((l) => inRange(l.at, range));
    const reread = () => void fillLog(out);
    const onRange = (nr: DateRange) => {
      range = nr;
      rangeChosen = true;
      setAddressQuery(range, { event: tab === 'christmas' ? null : tab });
      reread();
    };
    const info = eventInfo(tab);
    const kept = await keptLogPanel({
      reader: r,
      store: store!,
      range,
      matching: all.length,
      what: `from ${info.name}`,
      note: logNote,
      forgetText: forgetLogText(),
      onForget: () => {
        reader = null;
        store = null;
      },
      redraw: reread,
    });
    const picker = rangePicker(range, onRange);
    const v = values();
    const s = summariseEvent(shown, v);
    const now = Date.now();
    const csv = csvButton(() => `${tab}-${rangeText(range).replace(/[^\w]+/g, '-').toLowerCase()}.csv`, () => eventCsvRows(shown));

    const tiles = h(
      'div',
      { class: 'tiles' },
      tile('Log lines', num(s.lines), 'ev-lines'),
      tile('Items', num(s.items), 'ev-items'),
      tile('Items’ worth', money(s.value), 'ev-value'),
      tile('Money', money(s.money), 'ev-money'),
      ...(tab === 'halloween' ? [tile('Treats', s.treatLines ? num(s.treats) : '—', 'ev-treats')] : []),
    );
    const chart = box(h('h2', {}, tab === 'halloween' && s.treatLines ? 'Treats per day' : 'Worth per day'), zoomChart(sumPerDay(shown, (l) => (tab === 'halloween' && s.treatLines ? (l.treats ?? 0) : l.money + l.gained.reduce((a, i) => a + i.amount * (v.get(i.id) ?? 0), 0)), range, now), { label: tab === 'halloween' && s.treatLines ? 'Treats' : 'Worth', ...(tab === 'halloween' && s.treatLines ? {} : { format: (n: number) => money(n) }), onZoom: onRange }), { id: 'ev-chart' });
    const byItem = box(
      tab === 'easter' ? 'Items' : 'What you got',
      table(s.byItem.filter((r) => r.gained > 0), [
        { label: 'Item', get: (r) => itemName(r.id), sort: (r) => itemName(r.id) },
        { label: 'Got', get: (r) => num(r.gained), sort: (r) => r.gained, cls: 'r' },
        { label: 'Each', get: (r) => money(r.each), sort: (r) => r.each ?? -1, cls: 'r' },
        { label: 'Worth', get: (r) => money(r.value), sort: (r) => r.value ?? -1, cls: 'r' },
      ], { initial: 3, empty: 'No items in this range.' }),
      { id: 'ev-items' },
    );
    const byWhat = box(
      'By kind of line',
      table(s.byWhat, [
        { label: 'Torn’s log says', get: (w) => w.title, sort: (w) => w.title },
        { label: 'Times', get: (w) => num(w.count), sort: (w) => w.count, cls: 'r' },
        { label: 'Money', get: (w) => money(w.money), sort: (w) => w.money, cls: 'r' },
        { label: 'Items’ worth', get: (w) => money(w.value), sort: (w) => w.value, cls: 'r' },
      ], { initial: 1, empty: 'Nothing in this range.' }),
      { id: 'ev-what' },
    );
    // Every year kept (whatever the range): how this year compares.
    const years = summariseEvent(all, v).byYear;
    const byYear = box(
      'Every year',
      table(years, [
        { label: 'Year', get: (y) => String(y.year), sort: (y) => y.year },
        { label: 'Lines', get: (y) => num(y.lines), sort: (y) => y.lines, cls: 'r' },
        { label: 'Items', get: (y) => num(y.items), sort: (y) => y.items, cls: 'r' },
        { label: 'Items’ worth', get: (y) => money(y.value), sort: (y) => y.value, cls: 'r' },
        { label: 'Money', get: (y) => money(y.money), sort: (y) => y.money, cls: 'r' },
        ...(tab === 'halloween' ? [{ label: 'Treats', get: (y: (typeof years)[number]) => num(y.treats), sort: (y: (typeof years)[number]) => y.treats, cls: 'r' }] : []),
      ], { initial: 0, empty: 'Nothing kept yet — “Read older” reaches further back.' }),
      { id: 'ev-years' },
    );

    let special: Array<HTMLElement | null> = [];
    if (tab === 'easter') {
      const rows = eggRows(shown);
      special = [
        box('Eggs by colour', table(rows, [
          { label: 'Egg', get: (r) => itemName(r.id), sort: (r) => itemName(r.id) },
          { label: 'Found', get: (r) => num(r.found), sort: (r) => r.found, cls: 'r' },
          { label: 'Used', get: (r) => num(r.used), sort: (r) => r.used, cls: 'r' },
          { label: 'Worth each', get: (r) => money(v.get(r.id) ?? null), sort: (r) => v.get(r.id) ?? -1, cls: 'r' },
        ], { initial: 1, empty: 'No Easter eggs in this range.' }), { id: 'ev-eggs' }),
      ];
    } else if (tab === 'halloween') {
      special = [basketGuide(shown, v), treatsBox(all)];
    }
    const lines = box(
      'Log lines',
      [
        table(shown.slice(0, 500), [
          { label: 'When', get: (l) => h('span', { title: new Date(l.at).toUTCString() }, tct(l.at)), sort: (l) => l.at },
          { label: 'What', get: (l) => l.title, sort: (l) => l.title },
          { label: 'Got', get: (l) => l.gained.map((i) => `${itemName(i.id)}${i.amount > 1 ? ` ×${num(i.amount)}` : ''}`).join(', ') || (l.money ? money(l.money) : '—') },
          ...(tab === 'halloween' ? [{ label: 'Treats', get: (l: EventLine) => (l.treats === null ? '—' : num(l.treats)), sort: (l: EventLine) => l.treats ?? -1, cls: 'r' }] : []),
        ], { csv: false, initial: 0, empty: 'Nothing in this range.' }),
        shown.length > 500 ? h('p', { class: 'muted' }, `Showing the newest 500 of ${num(shown.length)} — the CSV has them all.`) : null,
        h('p', { class: 'muted small' }, 'Torn doesn’t describe its event log lines, so they’re read by their own titles and the items they name; anything unclear is listed by Torn’s own words.'),
      ].filter(Boolean) as HTMLElement[],
      { id: 'ev-lines', extra: csv },
    );
    out.replaceChildren(...([kept, picker, tiles, chart, ...special, h('div', { class: 'cards two' }, byItem, byWhat), byYear, lines].filter(Boolean) as HTMLElement[]));
  }

  // ------------------------------------------------------------ Trick or Treat: baskets and treats

  function basketGuide(lines: readonly EventLine[], v: ReadonlyMap<number, number | null>): HTMLElement {
    const mine = new Map(basketRows(lines, v).map((r) => [r.id, r]));
    // Torn's baskets (their names, descriptions and values from Torn's item list), with yours beside them.
    const known = HALLOWEEN_BASKET_IDS.filter((id) => items.has(id) || mine.has(id));
    const rows = known.map((id) => ({ id, item: items.get(id) ?? null, mine: mine.get(id) ?? null }));
    return box(
      'Basket guide',
      [
        table(rows, [
          { label: 'Basket', get: (r) => h('span', { title: r.item?.description ?? '' }, r.item?.name ?? `Item ${r.id}`), sort: (r) => r.item?.name ?? '' },
          { label: 'What Torn says', get: (r) => h('span', { class: 'small' }, r.item?.description || r.item?.effect || '—') },
          { label: 'Market value', get: (r) => money(r.item?.marketValue ?? null), sort: (r) => r.item?.marketValue ?? -1, cls: 'r' },
          { label: 'You opened', get: (r) => num(r.mine?.opened ?? 0), sort: (r) => r.mine?.opened ?? 0, cls: 'r' },
          { label: 'They gave (each)', get: (r) => (r.mine?.opened ? money((r.mine.gotValue + r.mine.gotMoney) / r.mine.opened) : '—'), sort: (r) => (r.mine?.opened ? (r.mine.gotValue + r.mine.gotMoney) / r.mine.opened : -1), cls: 'r' },
        ], { initial: 3, empty: 'Torn’s item list isn’t here yet.' }),
        h('p', { class: 'muted small' }, 'Names, descriptions and values are Torn’s own (its item list); “They gave” is what your own openings in this range gave, at market value.'),
      ],
      { id: 'ev-baskets' },
    );
  }

  function treatsBox(all: readonly EventLine[]): HTMLElement {
    const lite = treatRates(all, null, range);
    const full = fights ? treatRates(all, fights, range) : null;
    const r = full ?? lite;
    const rows: Array<[string, string]> = [
      ['Treats (from your log)', all.some((l) => l.treats !== null) ? num(r.treats) : 'your log lines don’t give treats as a number'],
      ['Treats a day (days with any)', r.perDay === null ? '—' : num(r.perDay, 1)],
    ];
    if (full) {
      rows.push(['Your attacks in these dates', `${num(full.attacks)} (${num(full.won)} won)`], ['Energy they cost', `${num(full.energy)} (${EVENT_ATTACK_ENERGY} each)`], ['Treats per hit', full.perHit === null ? '—' : num(full.perHit, 2)], ['Treats per 100 energy', full.per100Energy === null ? '—' : num(full.per100Energy, 1)]);
    }
    return box(
      'Energy to treats',
      [
        facts(rows),
        full ? h('p', { class: 'muted small' }, `Full: with your fights in the same dates (Torn’s last 100, plus what background tracking kept). ${fightsNote}`) : h('p', { class: 'muted small' }, keyMeets(session.get()?.access ?? null, FIGHTS_NEED) ? (fightsNote || 'Reading your fights…') : 'Lite: from your log only. A Limited key adds your fights — treats per hit and per 100 energy.'),
      ],
      { id: 'ev-treats' },
    );
  }

  // ------------------------------------------------------------ the event's dates and all time

  function drawTop(): HTMLElement {
    const info = eventInfo(tab);
    const w = calendar ? eventWindow(calendar, tab, Date.now()) : null;
    const when = !calendar
      ? h('p', { class: 'muted' }, calendarProblem || 'Reading Torn’s calendar…')
      : w
        ? h('p', { 'data-event-dates': '' }, `${w.entry.title}: ${day(w.entry.start)} – ${day(w.entry.end)} (Torn time)`, w.on ? h('strong', {}, ' · on now') : w.entry.start > Date.now() ? ` · in ${Math.ceil((w.entry.start - Date.now()) / 86_400_000)} days` : ' · last time')
        : h('p', { class: 'muted', 'data-event-dates': '' }, 'Not in Torn’s calendar right now.');
    const show = w ? h('button', { type: 'button', class: 'btn-small', 'data-action': 'event-dates' }, 'Show these dates') : null;
    show?.addEventListener('click', () => {
      range = w!.range;
      rangeChosen = true;
      setAddressQuery(range, { event: tab === 'christmas' ? null : tab });
      draw();
    });
    const all: Array<[string, string]> = [];
    if (tab === 'easter' && eggTotals) all.push(['Easter eggs found, all time', num(eggTotals.found)], ['Easter eggs used, all time', num(eggTotals.used)]);
    return box(
      info.name,
      [
        h('p', { class: 'muted' }, info.blurb),
        h('div', { class: 'toolbar' }, when, show),
        all.length ? facts(all) : null,
        tab === 'christmas' ? h('p', { class: 'muted small' }, 'On Christmas Town’s own page the Omni-Torn extension also keeps a finds list as you walk, and shows the items and chests near you.') : null,
        tab === 'easter' ? h('p', { class: 'muted small' }, `Torn’s eggs: ${EASTER_EGG_IDS.map(itemName).filter((n) => !/^Item \d/.test(n)).join(', ') || 'eleven colours'}.`) : null,
      ].filter(Boolean) as HTMLElement[],
      { id: 'ev-top' },
    );
  }

  // ------------------------------------------------------------ S38: hunting, the dump, city finds

  function drawFindsTop(kind: FindKind): HTMLElement {
    const info = findKindInfo(kind);
    const all: Array<[string, string]> = [];
    if (kind === 'dump' && foundTotals) all.push(['Items found in the dump, all time', num(foundTotals.dump)]);
    if (kind === 'city' && foundTotals) all.push(['Items found in the city, all time', num(foundTotals.city)]);
    let tiger: HTMLElement | null = null;
    if (kind === 'hunting') {
      const now = Date.now();
      const listed = (calendar ?? []).filter((c) => /tiger/i.test(c.title) && c.end > now).sort((a, b) => a.start - b.start)[0];
      const y = new Date(now).getUTCFullYear();
      const next = listed?.start ?? [Date.UTC(y, 6, 29), Date.UTC(y + 1, 6, 29)].find((d) => d + 86_400_000 > now)!;
      const on = next <= now;
      tiger = h('p', { 'data-tiger-next': '' }, `World Tiger Day: ${day(next)}${listed ? ' (Torn’s calendar)' : ''}`, on ? h('strong', {}, ' · today') : ` · in ${Math.ceil((next - now) / 86_400_000)} days`);
    }
    return box(info.name, [h('p', { class: 'muted' }, info.blurb), tiger, all.length ? facts(all) : null].filter(Boolean) as HTMLElement[], { id: 'ev-top' });
  }

  function draw(): void {
    if (finds) {
      const kind = finds;
      const what = { hunting: 'your log shows every hunt — what it brought in and cost.', dump: 'your log shows what searching the dump found.', city: 'your log shows the items you found in the city.' }[kind];
      body.replaceChildren(
        drawFindsTop(kind),
        gated('From your log', LOG_NEED, what, () =>
          findsPanel({
            pid,
            kind,
            range: () => range,
            onRange: (r) => {
              range = r;
              rangeChosen = true;
              setAddressQuery(range, { event: kind });
              draw();
            },
            items: () => items,
            calendar: () => calendar,
          }),
        ),
      );
      return;
    }
    const what = { christmas: 'your log shows what Christmas Town gave you, every year.', halloween: 'your log shows your treats and baskets.', easter: 'your log shows every egg you found and used.' }[tab];
    body.replaceChildren(drawTop(), gated('From your log', LOG_NEED, what, drawLog));
  }

  draw();
  // Torn's calendar (public, your key), Torn's items (the server's list, none of your calls), your eggs all time (one call),
  // and — for Trick or Treat with a Limited key — your fights (Torn's last 100 and what tracking kept).
  status.textContent = 'Reading Torn’s calendar and items…';
  const access = session.get()?.access ?? null;
  const reads = await Promise.allSettled([
    torn('/v2/torn/calendar', {}, 60 * 60_000),
    ref('items'),
    torn('/v2/user/personalstats', { cat: 'items' }, 10 * 60_000),
    keyMeets(access, FIGHTS_NEED) ? Promise.all([torn('/user/', { selections: 'attacks' }), server<{ attacks: Record<string, unknown> }>('GET', '/api/me/attacks?days=90').catch(() => null)]) : Promise.resolve(null),
  ]);
  const [cal, itemList, eggs, attacks] = reads;
  if (cal.status === 'fulfilled') calendar = readTornCalendar(cal.value);
  else {
    calendar = [];
    calendarProblem = `Torn’s calendar couldn’t be read: ${(cal.reason as Error)?.message ?? 'no answer'}.`;
  }
  if (itemList.status === 'fulfilled') items = new Map(itemList.value.data.map((i) => [i.id, i]));
  if (eggs.status === 'fulfilled') {
    eggTotals = readEggTotals(eggs.value);
    foundTotals = readFoundTotals(eggs.value);
  }
  if (attacks.status === 'fulfilled' && attacks.value) {
    const [live, kept] = attacks.value;
    const list = readAttacks({ attacks: { ...(kept?.attacks ?? {}), ...((live as { attacks?: Record<string, unknown> }).attacks ?? {}) } }, pid).filter((a) => a.outgoing);
    fights = list.map((a) => ({ at: a.started, won: a.won }));
    fightsNote = fights.length ? `${num(fights.length)} fights of yours known, the oldest ${day(Math.min(...fights.map((f) => f.at)))}.` : 'No fights of yours known.';
  } else if (attacks.status === 'rejected') fightsNote = `Your fights couldn’t be read: ${(attacks.reason as Error)?.message ?? 'no answer'}.`;
  status.textContent = '';
  if (!rangeChosen && !finds) range = windowRange() ?? range;
  draw();
}
